import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Card, Kpi, KpiLedger } from '../../components/shared/Card';
import { RouteStrip } from '../../components/shared/People';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { Sankey, useTableToggle } from '../../components/shared/charts';
import { analyticsApi, rangeText, routeSteps, useAnalytics } from '../../services/analyticsApi';
import { callsLink } from '../calls/callFilters';

const COLOR = {
  AutoAttendant: 'var(--route-aa)',
  Queue: 'var(--route-queue)',
  Agent: 'var(--route-agent)',
  Transfer: 'var(--route-transfer)',
  Voicemail: 'var(--route-voicemail)',
  Abandoned: 'var(--route-abandon)',
  Forward: 'var(--route-forward)',
  External: 'var(--route-forward)',
};

const LEGEND = [
  ['Auto attendant', COLOR.AutoAttendant],
  ['Queue', COLOR.Queue],
  ['Agent', COLOR.Agent],
  ['Transfer', COLOR.Transfer],
  ['Voicemail', COLOR.Voicemail],
  ['Abandoned', COLOR.Abandoned],
  ['Forward', COLOR.Forward],
];

const COLUMN_TITLES = ['Entry point', 'Queue', 'Outcome', 'After transfer'];

/** The call log filtered to the queues a route's calls went through; null for a route without a queue. */
export function routeCallsLink(route) {
  const queueIds = route.queueIds || [];
  if (!queueIds.length) return null;
  return callsLink({ queueIds, queueName: (route.steps || []).find((s) => s.type === 'Queue')?.name });
}

function SankeyCard({ data, range }) {
  const table = useTableToggle();
  const index = Object.fromEntries(data.nodes.map((n, i) => [n.id, i]));
  const nodes = data.nodes.map((n) => ({ name: n.name, col: n.column, color: COLOR[n.type] || COLOR.Queue, kind: n.kind }));
  const links = data.links.filter((l) => index[l.source] != null && index[l.target] != null).map((l) => ({ s: index[l.source], t: index[l.target], v: l.callCount, color: nodes[index[l.target]].color }));
  const columns = Math.max(0, ...data.nodes.map((n) => n.column)) + 1;
  return (
    <Card
      title="Call routes"
      description={`${fmt.int(data.totals.callsRouted)} calls, ${rangeText(range.from, range.to)}. Hover a band for counts; widths are proportional to calls.`}
      actions={
        <>
          <div className="legend">
            {LEGEND.map(([name, color]) => (
              <span key={name}>
                <i style={{ background: color }} />
                {name}
              </span>
            ))}
          </div>
          {table.button}
        </>
      }
    >
      <div className="table-wrap" style={{ overflowY: 'hidden' }}>
        <div style={{ minWidth: 760 }}>
          <Sankey nodes={nodes} links={links} height={520} labelSpace={230} colTitles={COLUMN_TITLES.slice(0, columns)} aria="Sankey diagram of call routes" showTable={table.on} />
        </div>
      </div>
    </Card>
  );
}

export function CallFlowPage() {
  const h = useHierarchyFilter();
  const navigate = useNavigate();
  const { data, error, retry } = useAnalytics(() => analyticsApi.callFlow(h.params), JSON.stringify(h.params));

  let body;
  if (error) body = <ErrorState title="Call flow couldn't load" body="Vertolytics couldn't reach the analytics service." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={520} />;
  else if (!data.totals.callsRouted)
    body = <EmptyState icon="flow" title="No routed calls in this period" body="Call flow needs calls that passed through at least one auto attendant or queue. Clear filters or widen the date range." />;
  else {
    const t = data.totals;
    body = (
      <div className="stack-lg">
        <KpiLedger>
          <Kpi label="Calls routed" value={fmt.int(t.callsRouted)} meta={`${t.entryPoints} ${t.entryPoints === 1 ? 'entry point' : 'entry points'}`} />
          <Kpi label="Average steps per call" value={Number(t.averageSteps).toFixed(1)} meta="auto attendant, queue, agent" />
          <Kpi label="Transferred" value={`${Number(t.transferredPercent).toFixed(1)}%`} meta={`${fmt.int(t.transferred)} calls`} />
          <Kpi label="Two or more transfers" value={fmt.int(t.twoOrMoreTransfers)} meta={`${Number(t.twoOrMoreTransfersPercent).toFixed(1)}% of calls`} />
          <Kpi label="Reached voicemail" value={`${Number(t.voicemailPercent).toFixed(1)}%`} meta={`${fmt.int(t.voicemail)} calls`} />
        </KpiLedger>
        <SankeyCard data={data} range={h.range} />
        <Card flush title="Most common routes" description="Each dot is one step in the call; click a route to see the calls in its queue">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th className="n">Calls</th>
                  <th className="n">Share</th>
                  <th className="n">Avg wait</th>
                  <th className="n">Avg talk</th>
                  <th>Transfers</th>
                </tr>
              </thead>
              <tbody>
                {data.routes.map((r) => {
                  const link = routeCallsLink(r);
                  const open = () => link && navigate(link);
                  return (
                    <tr key={r.label} className={link ? 'clickable' : undefined} tabIndex={link ? 0 : undefined} onClick={open} onKeyDown={(e) => e.key === 'Enter' && open()}>
                      <td>
                        <div className="row" style={{ gap: 12 }}>
                          <RouteStrip steps={routeSteps(r.steps)} />
                          <span className="small">{r.label}</span>
                        </div>
                      </td>
                      <td className="n">{fmt.int(r.calls)}</td>
                      <td className="n">{Number(r.sharePercent).toFixed(1)}%</td>
                      <td className="n">{r.averageWaitSeconds ? fmt.mmss(r.averageWaitSeconds) : '—'}</td>
                      <td className="n">{r.averageTalkSeconds ? fmt.mmss(r.averageTalkSeconds) : '—'}</td>
                      <td>
                        {r.transfers ? (
                          <span className="badge b-warning">
                            <Icon name="forward" />
                            {r.transfers}
                          </span>
                        ) : (
                          <span className="caption">None</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Page
      title="Call flow"
      subtitle="How calls move from the number dialed, through auto attendants and queues, to an agent, voicemail, or a hang-up. Built from every segment of each Teams call record."
      exportConfig={{ name: 'Call flow', endpoint: '/export/datasets/CallJourneyHops', params: { title: 'Call flow' } }}
    >
      {body}
    </Page>
  );
}
